import * as React from "react";
import {
  SimpleForm,
  NumberInput,
  useNotify,
  TextInput,
  maxValue,
} from "react-admin";
import {
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Stack,
  Container,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useWatch, useFormContext } from "react-hook-form";
import { GdprBanner } from "../GdprBanner";
import {
  COUNT_AIRBAG_MAX,
  COUNT_BINS_MAX,
  integerOnly,
} from "../../validators";
import { IfNeededAlert } from "../partials/ifNeededAlert";

const API_URL =
  process.env.REACT_APP_API_URL || "https://carebag.hubency.com/api";

interface OrderResponse {
  order: {
    _id: string;
    orderNumber: string;
    status: string;
    provisionalDate: string;
    client: {
      name: string;
      internalCode: string;
      address: string;
      zipCode: string;
      city: string;
    };
    provider: { name: string };
    latestClientInventory: InventoryRecord;
  };
}

interface InventoryRecord {
  id: string;
  airbagsCount: number;
  fullBinsCount: number;
  emptyBinsCount: number;
  createdAt: string;
  createdFrom: string;
}

const NUMBER_AIRBAG_IN_BIN = 52;

/** Custom field for calculating remaining airbags */
const FullBinsCollectedInput: React.FC<{
  source: string;
  label: string;
  latestInventory?: InventoryRecord;
}> = ({ source, label, latestInventory }) => {
  const { setValue } = useFormContext();
  const fullBins = useWatch({ name: source });

  React.useEffect(() => {
    if (latestInventory) {
      if (fullBins > latestInventory.fullBinsCount) {
        const rem =
          latestInventory.airbagsCount -
          (fullBins - latestInventory.fullBinsCount) * NUMBER_AIRBAG_IN_BIN;
        setValue("airbagsRemainingCount", Math.max(rem, 0), {
          shouldValidate: true,
          shouldDirty: true,
        });
      } else {
        setValue("airbagsRemainingCount", latestInventory.airbagsCount, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [fullBins, latestInventory, setValue]);

  return (
    <NumberInput
      source={source}
      label={label}
      validate={[
        maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
        integerOnly("Doit être un nombre entier"),
      ]}
    />
  );
};

/** Main page */
export const ExternalsOrderCollect: React.FC = () => {
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("inviteToken") || "";
  const notify = useNotify();

  const [loading, setLoading] = React.useState(true);
  const [latestInventory, setLatestInventory] =
    React.useState<InventoryRecord>();
  const [error, setError] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState<OrderResponse["order"] | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [certified, setCertified] = React.useState(true);
  const [thankYouOpen, setThankYouOpen] = React.useState(false);

  // Load order information
  React.useEffect(() => {
    if (!token) {
      setError("Lien invalide");
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/orders/invite?inviteToken=${token}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Échec récupération (${res.status})`);
        return res.json() as Promise<OrderResponse>;
      })
      .then((data) => {
        setLatestInventory(data.order.latestClientInventory);
        setOrder(data.order);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  // Form submission
  const onSubmit = async (values: any) => {
    try {
      const res = await fetch(`${API_URL}/orders/${order!._id}/collect`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Invite-Token": token,
        },
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || res.statusText);
      }
      notify("Collecte enregistrée avec succès ✅", { type: "info" });
      setFormOpen(false);
      setThankYouOpen(true);
    } catch (err: any) {
      notify(`Erreur : ${err.message}`, { type: "error" });
    }
  };

  const initialFormValues = latestInventory
    ? {
        airbagsRemainingCount: latestInventory.airbagsCount,
        fullBinsCollectedCount: latestInventory.fullBinsCount,
        emptyBinsDepositCount: 0,
        orderId: order!._id,
      }
    : {};

  /** Loading */
  if (loading) return <CircularProgress sx={{ m: "auto", display: "block" }} />;

  /** Error */
  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );

  /** Order status */
  if (order?.status === "CANCELLED")
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Le bon de commande {order.orderNumber} a été annulé. Merci de contacter
        votre interlocuteur.
      </Alert>
    );

  if (order?.status === "COMPLETED")
    return (
      <Alert severity="success" sx={{ m: 2 }}>
        Le bon de commande {order.orderNumber} est déjà validé ✅
      </Alert>
    );

  return (
    <>
      <Container maxWidth="md">
        <title>Hubency - Carebag Enregistrer la collecte</title>
        <Paper sx={{ p: 3, m: 2 }}>
          <Typography variant="h5" align="center" gutterBottom>
            Bon de Commande : {order!.orderNumber}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Client :</strong> {order?.client.name} <br />
            <strong>N° client :</strong> {order?.client.internalCode} <br />
            <strong>Adresse :</strong> {order?.client.address},{" "}
            {order?.client.zipCode} {order?.client.city}
          </Typography>

          <Typography sx={{ mb: 1 }}>
            <strong>Prestataire :</strong> {order!.provider.name}
          </Typography>

          <Typography>
            <strong>Date prévisionnelle :</strong>{" "}
            {new Date(order!.provisionalDate).toLocaleDateString("fr-FR")}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Inventaire */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            {[
              {
                label: "Airbags en vrac",
                value: latestInventory?.airbagsCount,
              },
              {
                label: "Caisses pleines",
                value: latestInventory?.fullBinsCount,
              },
              {
                label: "Caisses vides",
                value: latestInventory?.emptyBinsCount,
              },
            ].map((item, i) => (
              <Card key={i} sx={{ flex: 1, textAlign: "center" }}>
                <CardContent>
                  <Typography variant="subtitle2">{item.label}</Typography>
                  <Typography variant="h6">{item.value ?? 0}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* Bouton */}
          <Button
            variant="outlined"
            color="primary"
            fullWidth={true}
            onClick={() => setFormOpen(true)}
          >
            Enregistrer une collecte
          </Button>
        </Paper>
        <IfNeededAlert />
      </Container>

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enregistrement de la collecte</DialogTitle>
        <DialogContent>
          <SimpleForm
            id="collect-form" // 👈 1. on donne un id
            defaultValues={initialFormValues}
            onSubmit={onSubmit}
            toolbar={false}
          >
            <FullBinsCollectedInput
              source="fullBinsCollectedCount"
              label="Caisses pleines récupérées"
              latestInventory={latestInventory}
            />
            <NumberInput
              source="emptyBinsDepositCount"
              label="Caisses vides déposées"
              validate={[
                maxValue(COUNT_BINS_MAX, `Maximum ${COUNT_BINS_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <NumberInput
              source="airbagsRemainingCount"
              label="Airbags en vrac restants"
              validate={[
                maxValue(COUNT_AIRBAG_MAX, `Maximum ${COUNT_AIRBAG_MAX}`),
                integerOnly("Doit être un nombre entier"),
              ]}
            />
            <TextInput
              source="comment"
              label="Commentaire (optionnel)"
              fullWidth
              multiline
            />
            <TextInput
              source="orderId"
              defaultValue={order!._id}
              style={{ display: "none" }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={certified}
                  onChange={(e) => setCertified(e.target.checked)}
                />
              }
              label="Je certifie que les données saisies sont exactes"
              sx={{ mt: 2 }}
            />
          </SimpleForm>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFormOpen(false)}
            color="warning"
            variant="outlined"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            form="collect-form"
            color="primary"
            variant="outlined"
            disabled={!certified}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={thankYouOpen}
        onClose={() => setThankYouOpen(false)}
        aria-labelledby="thank-you-dialog"
      >
        <DialogTitle id="thank-you-dialog">Merci 🎉</DialogTitle>
        <DialogContent>
          <Typography>
            Vos données de collecte ont bien été transmises.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            L'équipe Hubency vous souhaite une excellente journée 🌿
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setThankYouOpen(false)} autoFocus>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
      <GdprBanner />
    </>
  );
};
